import {DatePicker, Input, TimePicker} from 'antd'
import {WeekdaysField} from 'components/form/fields/WeekdaysField'
import {ChipGroup, Field, FormActions, Section} from 'components/form/ModernForm'
import {Formik} from 'formik'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import {SelectWidget} from '../../custom/ReactSelectComponents'
import {IoCalendarOutline, IoDocumentTextOutline, IoInformationCircleOutline, IoTimeOutline} from 'react-icons/io5'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {IoIosTimer} from 'react-icons/io'

dayjs.extend(customParseFormat)

// Options dùng cho ChipGroup (single-select, 2 lựa chọn cố định) - thay cho Radio.Group/BooleanPicker cũ.
const specialEventOptions = [
    {value: 0, label: 'normal mass'},
    {value: 1, label: 'special mass'},
]

const massOptions = [
    {value: 1, label: 'sunday mass'},
    {value: 0, label: 'daily mass'},
]

const languageOptions = ['vi', 'en', 'fr', 'zh', 'ko']

// Field/component đơn giản, không cần tách khỏi formSchema cũ (không có LookupField/cascade phức
// tạp) - viết thẳng theo pattern Section/Field của ModernForm.js, cùng cách đã dùng ở
// ParishForm.js/YellowPageForm.js. Xem docs/ui/design-principles.md "Pattern: Inline CRUD trong list".
const FormBody = ({formik, submitText, onCancel}) => {
    const t = useTranslate()
    const {values, touched, errors, handleSubmit, isSubmitting, isValid, setFieldValue} = formik
    const onChange = (e) => setFieldValue(e.target.name, e.target.value)
    const weekdaysStr = values.weekdays ?? '0000000'

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Section icon={IoInformationCircleOutline} title={t('basic information')} theme="highlight">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                        <Field label={t('title')} required error={touched.title && errors.title}>
                            <Input name="title" value={values.title} onChange={onChange} placeholder={t('title')}/>
                        </Field>
                    </div>
                    <Field label={t('language')} required>
                        <SelectWidget
                            name="language"
                            value={values.language}
                            allowClear={false}
                            className="w-full"
                            placeholder={t('language')}
                            onChange={(value) => setFieldValue('language', value ?? null)}
                            options={languageOptions.map((code) => ({label: t(code), value: code}))}
                        />
                    </Field>
                </div>
            </Section>

            <Section icon={IoTimeOutline} title={t('mass times')}>
                <Field label={t('mass type')}>
                    <ChipGroup
                        options={specialEventOptions}
                        value={values.special_event_flag}
                        onChange={(v) => setFieldValue('special_event_flag', v)}
                    />
                </Field>

                {values.special_event_flag ? (
                    <Field label={t('event date time')}>
                        <Translate>
                            {(tt) => (
                                <DatePicker
                                    showTime
                                    name="special_event_datetime"
                                    className="w-full"
                                    format={'HH:mm - DD/MM/YYYY'}
                                    value={values.special_event_datetime ? dayjs(new Date(values.special_event_datetime)) : null}
                                    suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                                    placeholder={tt('ex: 07:00 - 20/10/2010')}
                                    onChange={(date) => {
                                        setFieldValue('special_event_datetime', date ? date.format() : undefined)
                                    }}
                                />
                            )}
                        </Translate>
                    </Field>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label={t('mass category')}>
                                <ChipGroup
                                    options={massOptions}
                                    value={values.sunday_mass_flag}
                                    onChange={(v) => setFieldValue('sunday_mass_flag', v)}
                                />
                            </Field>
                            <Field label={t('start time')}>
                                <TimePicker
                                    value={values.start_time ? dayjs(values.start_time, 'HH:mm') : undefined}
                                    className="w-full"
                                    format="HH:mm"
                                    changeOnScroll
                                    needConfirm={false}
                                    suffixIcon={<IoIosTimer className="text-color-500"/>}
                                    placeholder={t('ex: 07:00')}
                                    onChange={(_, timeString) => setFieldValue('start_time', timeString)}
                                />
                            </Field>
                        </div>
                        <Field label={t('recurring days')}>
                            <WeekdaysField
                                value={weekdaysStr}
                                onChange={(next) => setFieldValue('weekdays', next)}
                            />
                        </Field>
                    </>
                )}
            </Section>

            <Section icon={IoDocumentTextOutline} title={t('notes')} subtitle={t('optional')}>
                <Field label={t('description')}>
                    <Input.TextArea
                        rows={3}
                        name="description"
                        value={values.description}
                        onChange={onChange}
                        placeholder={t('description')}
                    />
                </Field>
            </Section>

            <FormActions onCancel={onCancel} submitText={submitText} isSubmitting={isSubmitting} isValid={isValid}/>
        </form>
    )
}

const validate = (values, t) => {
    const errors = {}
    _.forEach(['title', 'language'], (name) => {
        const value = _.get(values, name)
        if (_.isEmpty(value ?? undefined)) {
            errors[name] = t('required field')
        }
    })
    return errors
}

export const CreateChurchMassTimeForm = ({
                                              onCancel = NullAsDefault,
                                              onSubmit = NullAsDefault,
                                          }) => {
    const t = useTranslate()
    return (
        <Formik
            initialValues={{
                language: 'vi',
                title: undefined,
                description: undefined,
                weekdays: '0000000',
                start_time: '07:00',
                sunday_mass_flag: 0,
                special_event_flag: 0,
                special_event_datetime: undefined,
            }}
            validate={(values) => validate(values, t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            onSubmit={(values) => {
                onSubmit(_.omitBy(values, _.isUndefined))
            }}>
            {(formik) => <FormBody formik={formik} submitText="create" onCancel={onCancel}/>}
        </Formik>
    )
}

export const EditChurchMassTimeForm = ({
                                            item,
                                            onCancel = NullAsDefault,
                                            onSubmit = NullAsDefault,
                                        }) => {
    const t = useTranslate()
    const initialValues = useMemo(() => ({...item}), [item])
    return (
        <Formik
            validate={(values) => validate(values, t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const change = shallowDiff(values, initialValues)
                onSubmit(_.omitBy(change, _.isUndefined))
            }}>
            {(formik) => <FormBody formik={formik} submitText="save" onCancel={onCancel}/>}
        </Formik>
    )
}
